import axios from 'axios';

const API_ROOT = (process.env.REACT_APP_API_URL || 'http://localhost:8000/api').replace(/\/api$/, '');

export const getImageUrl = (path) => {
    if (!path) return null;
    if (path.startsWith('http')) return path;
    return `${API_ROOT}/storage/${path}`;
};

const api = axios.create({
    baseURL: process.env.REACT_APP_API_URL || 'http://localhost:8000/api',
    headers: { 'Content-Type': 'application/json' },
});

api.interceptors.request.use((config) => {
    const token = localStorage.getItem('token');
    if (token) config.headers.Authorization = `Bearer ${token}`;
    return config;
});

api.interceptors.response.use(
    (res) => res,
    (err) => {
        if (err.response?.status === 401) {
            localStorage.removeItem('token');
            window.location.href = '/login';
        }
        return Promise.reject(err);
    }
);

export const authService = {
    register: (data) => api.post('/auth/register', data),
    login: (email, password) => api.post('/auth/login', { email, password }),
    logout: () => api.post('/auth/logout'),
    me: () => api.get('/auth/me'),
};

const multipart = { headers: { 'Content-Type': 'multipart/form-data' } };

export const protestService = {
    list:        ()         => api.get('/protests'),
    get:         (id)       => api.get(`/protests/${id}`),
    create:      (data)     => api.post('/protests', data, multipart),
    createManual:(data)     => api.post('/protests/manual', data, multipart),
    update:      (id, data) => api.put(`/protests/${id}`, data),
    delete:      (id)       => api.delete(`/protests/${id}`),
    analyzeText: (text)     => api.post('/protests/analyze-text', { text }),
};


export const sourceService = {
    list:   (protestId) => api.get(`/protests/${protestId}/sources`),
    create: (protestId, data) => api.post(`/protests/${protestId}/sources`, data),
    delete: (protestId, sourceId) => api.delete(`/protests/${protestId}/sources/${sourceId}`),
};

export const storyService = {
    list:   (protestId) => api.get(`/protests/${protestId}/stories`),
    create: (protestId, data) => api.post(`/protests/${protestId}/stories`, data),
    update: (protestId, storyId, data) => api.put(`/protests/${protestId}/stories/${storyId}`, data),
    delete: (protestId, storyId) => api.delete(`/protests/${protestId}/stories/${storyId}`),
};

export const reportService = {
    list:        ()            => api.get('/reports'),
    get:         (id)          => api.get(`/reports/${id}`),
    preview:     (data)        => api.post('/reports/preview', data),
    save:        (data)        => api.post('/reports', data),
    delete:      (id)          => api.delete(`/reports/${id}`),
    generatePDF: (id, html)    => api.post(`/reports/${id}/pdf`, { html_content: html }),
    downloadPDF: (id)          => api.get(`/reports/${id}/pdf`, { responseType: 'blob' }),
};

export const trashService = {
    list:            () => api.get('/trash'),
    restore:         (protestId) => api.put(`/trash/${protestId}/restore`),
    permanentDelete: (protestId, reason) => api.delete(`/trash/${protestId}/permanent`, { data: { reason } }),
};

export const statsService = {
    overview: () => api.get('/stats/overview'),
    byGovernorate: () => api.get('/stats/by-governorate'),
    byCategory: () => api.get('/stats/by-category'),
    crowdTrends: () => api.get('/stats/crowd-trends'),
    comprehensive: () => api.get('/stats/comprehensive'),
};

export const categoryService = {
    list: () => api.get('/categories'),
    create: (name) => api.post('/categories', { name }),
    createSub: (name, category_id) => api.post('/sub-categories', { name, category_id }),
};

export const governorateService = {
    list: () => api.get('/governorates'),
    regions: (id) => api.get(`/governorates/${id}/regions`),
};

export const adminService = {
    listUsers: () => api.get('/admin/users'),
    updateUser: (id, data) => api.put(`/admin/users/${id}`, data),
    createAdmin: (data) => api.post('/admin/users', data),
    governorates: () => api.get('/admin/governorates'),
    storeGovernorate: (name) => api.post('/admin/governorates', { name }),
    updateGovernorate: (id, name) => api.put(`/admin/governorates/${id}`, { name }),
    destroyGovernorate: (id) => api.delete(`/admin/governorates/${id}`),
    regions: () => api.get('/admin/regions'),
    storeRegion: (data) => api.post('/admin/regions', data),
    updateRegion: (id, name) => api.put(`/admin/regions/${id}`, { name }),
    destroyRegion: (id) => api.delete(`/admin/regions/${id}`),
    categories: () => api.get('/admin/categories'),
    storeCategory: (name) => api.post('/admin/categories', { name }),
    storeSubCategory: (data) => api.post('/admin/sub-categories', data),
    updateCategory: (id, name) => api.put(`/admin/categories/${id}`, { name }),
    destroyCategory: (id) => api.delete(`/admin/categories/${id}`),
    updateSubCategory: (id, name) => api.put(`/admin/sub-categories/${id}`, { name }),
    destroySubCategory: (id) => api.delete(`/admin/sub-categories/${id}`),
};

export const telegramService = {
    getLinkCode: () => api.post('/auth/telegram/link-code'),
};

export default api;
